import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import {
  useCourseEditCore,
  type BasicInfoFormData,
  type UseCourseEditCoreReturn,
} from '@edusense/features-course-edit-core';
import { useCurriculumStore } from '@edusense/entities-course';
import type { SectionData } from '../components/CurriculumTabContent';
import type { CourseDraftDetailResponse } from '@edusense/course-client';
import {
  saveAutosaveData,
  loadAutosaveData,
  loadAutosaveEntry,
  clearAutosaveData,
  hasAutosaveData,
} from '../utils/autosave';
import { debounce } from '../utils/debounce';

const handledAutosavePrompts = new Map<string, number>();
const LANGUAGE_STATE_SESSION_KEY_PREFIX = 'course_edit_language_state_';
const LANGUAGE_TRANSLATIONS_SESSION_KEY_PREFIX = 'course_edit_language_translations_';
const CURRICULUM_SNAPSHOT_SESSION_KEY_PREFIX = 'course_edit_curriculum_snapshot_';

interface PersistedLanguageState {
  currentLanguage: string;
  supportedLanguages: string[];
}

type PersistedTranslationMap = Record<string, BasicInfoTranslationEntry>;
type PersistedCurriculumSnapshot = Record<string, SectionData[]>;

const loadPersistedLanguageState = (courseId: string | undefined | null): PersistedLanguageState | null => {
  if (!courseId) {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(`${LANGUAGE_STATE_SESSION_KEY_PREFIX}${courseId}`);
    return raw ? JSON.parse(raw) as PersistedLanguageState : null;
  } catch {
    return null;
  }
};

const persistLanguageState = (courseId: string | undefined | null, state: PersistedLanguageState) => {
  if (!courseId) {
    return;
  }
  try {
    sessionStorage.setItem(`${LANGUAGE_STATE_SESSION_KEY_PREFIX}${courseId}`, JSON.stringify(state));
  } catch {
    // sessionStorage may be unavailable; ignore.
  }
};

const loadPersistedLanguageTranslations = (
  courseId: string | undefined | null,
): PersistedTranslationMap | null => {
  if (!courseId) {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(`${LANGUAGE_TRANSLATIONS_SESSION_KEY_PREFIX}${courseId}`);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object') {
      return parsed as PersistedTranslationMap;
    }
  } catch {
    return null;
  }
  return null;
};

const persistLanguageTranslations = (
  courseId: string | undefined | null,
  translations: PersistedTranslationMap,
) => {
  if (!courseId) {
    return;
  }
  try {
    sessionStorage.setItem(
      `${LANGUAGE_TRANSLATIONS_SESSION_KEY_PREFIX}${courseId}`,
      JSON.stringify(translations),
    );
  } catch {
    // sessionStorage may be unavailable; ignore.
  }
};

const persistCurriculumSnapshots = (
  courseId: string | undefined | null,
  snapshots: PersistedCurriculumSnapshot,
) => {
  if (!courseId) {
    return;
  }
  try {
    sessionStorage.setItem(
      `${CURRICULUM_SNAPSHOT_SESSION_KEY_PREFIX}${courseId}`,
      JSON.stringify(snapshots),
    );
  } catch {
    // sessionStorage may be unavailable; ignore.
  }
};

/**
 * 🏗️ FSD Features Layer - Course Edit Logic Hook
 *
 * 일반 CourseEditPage에서 사용하는 비즈니스 로직
 * - React Hook Form 관리 (일반 버전)
 * - 폼 제출 처리
 * - course-edit-core의 공통 로직과 통합
 */

const normalizeLanguageCode = (code?: string | null): string | null => {
  if (!code) {
    return null;
  }
  const trimmed = `${code}`.trim();
  if (!trimmed) {
    return null;
  }
  return trimmed.toLowerCase();
};

const cloneSectionsShallow = (sections: SectionData[] = []): SectionData[] =>
  (sections || []).map(section => ({
    ...section,
    lessons: (section.lessons || []).map(lesson => ({ ...lesson })),
  }));

type BasicInfoTranslationEntry = {
  title: string;
  description?: string | null;
};

export type BasicInfoTranslationSummary = Record<string, {
  isTitleMissing: boolean;
  isDescriptionMissing: boolean;
}>;

export interface UseCourseEditLogicReturn extends UseCourseEditCoreReturn {
  /** React Hook Form methods */
  formMethods: ReturnType<typeof useForm<BasicInfoFormData>>;
  /** 폼 제출 핸들러 */
  onSubmit: (data: BasicInfoFormData) => Promise<void>;
  /** API로부터 로드된 초기 섹션 데이터 */
  initialSections?: SectionData[];
  /** 언어별 초기 섹션 데이터 */
  initialLocalizedSections?: Record<string, SectionData[]>;
  /** 기본 정보 편집에 사용 중인 언어 코드 */
  currentBasicInfoLanguage: string;
  /** 기본 정보에서 사용 가능한 언어 목록 (정규화된 코드) */
  basicInfoAvailableLanguages: string[];
  /** 기본 정보 번역 상태 요약 */
  basicInfoTranslationSummary: BasicInfoTranslationSummary;
  /** 기본 정보 편집 언어 변경 */
  changeBasicInfoLanguage: (languageCode: string) => void;
  /** 기본 언어 내용을 현재 언어로 복사 */
  copyBasicInfoFromDefault: () => void;
  /** 지원 언어/기본 언어 변경 시 내부 번역 맵 동기화 */
  syncBasicInfoLanguageMetadata: (languages: string[], defaultLanguage?: string | null) => void;
  /** 저장되지 않은 변경사항을 마지막 저장 상태로 되돌림 */
  resetAllChanges: () => Promise<void>;
  /** 강제 리셋 시 하위 뷰를 재마운트하기 위한 시그널 */
  resetSignal: number;
  /** 저장되지 않은 변경 여부 */
  hasUnsavedChanges: boolean;
  /** 다음 beforeunload 경고를 우회 */
  bypassNextBeforeUnload: () => void;
  /** 임시저장 복원 모달 상태 */
  autosaveRestoreModal: {
    isOpen: boolean;
    courseId: string | null;
    onConfirm: () => void;
    onDiscard: () => void;
  };
  /** 현재 폼 상태를 즉시 임시저장 스냅샷으로 기록 */
  flushAutosaveSnapshot: () => boolean;
}

export const useCourseEditLogic = (): UseCourseEditLogicReturn => {
  // 공통 로직 사용
  const coreLogic = useCourseEditCore();
  const currentCourseId = coreLogic.courseId;
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const skipNextBeforeUnloadRef = useRef(false);
  const [autosaveModalState, setAutosaveModalState] = useState<{
    isOpen: boolean;
    courseId: string | null;
  }>({
    isOpen: false,
    courseId: null,
  });
  const pendingAutosaveTimestampRef = useRef<number | null>(null);

  // 임시저장 복원 완료 플래그
  const autosaveRestoredRef = useRef<boolean>(false);
  const setCurriculumDirty = useCurriculumStore(state => state.setIsCurriculumDirty);
  const resetCurriculumState = useCurriculumStore(state => state.resetCurriculumState);
  const initializeCurriculumSections = useCurriculumStore(state => state.initializeSections);
  const primeLanguageSnapshot = useCurriculumStore(state => state.primeLanguageSnapshot);
  const lastSavedBasicInfoRef = useRef<BasicInfoFormData | null>(null);
  const lastSavedCurriculumSnapshotsRef = useRef<Record<string, SectionData[]>>({});
  const [resetSignal, setResetSignal] = useState(0);
  const [initialSections, setInitialSections] = useState<SectionData[] | undefined>(undefined);
  const [initialLocalizedSections, setInitialLocalizedSections] = useState<Record<string, SectionData[]> | undefined>(undefined);

  const sanitizeTitle = useCallback((value?: string | null) => {
    const trimmed = (value ?? '').trim();
    return trimmed.length > 0 ? trimmed : '제목 없음';
  }, []);

  // 일반 버전 폼 관리
  const formMethods = useForm<BasicInfoFormData>({
    defaultValues: {
      title: '제목 없음',
      description: '',
      level: '', // 자유 텍스트 입력
      thumbnailUrl: undefined,
      isPublic: true,
      allowComments: true,
      defaultLanguage: 'ko',
      supportedLanguages: ['ko'],
      draftVersion: null,
      priceAmount: 0,
      priceCurrency: 'KRW',
      isFree: true,
    },
  });
  
  const { reset } = formMethods;
  const watchedBasicInfo = useWatch({
    control: formMethods.control,
    name: ['title', 'description', 'level', 'priceAmount', 'priceCurrency', 'isFree'],
  });
  const hasInitializedAutosaveRef = useRef(false);

  // 임시저장을 위한 디바운스된 저장 함수
  const lastAutosavedSnapshotRef = useRef<string | null>(null);

  const [basicInfoAvailableLanguages, setBasicInfoAvailableLanguages] = useState<string[]>(['ko']);
  const [currentBasicInfoLanguage, setCurrentBasicInfoLanguage] = useState<string>('ko');
  const [basicInfoTranslationsVersion, setBasicInfoTranslationsVersion] = useState<number>(0);

  const getSupportedLanguagesSnapshot = useCallback(() => {
    const raw = formMethods.getValues('supportedLanguages');
    const fromForm = Array.isArray(raw) ? raw : (raw ? [raw] : []);
    const normalized = fromForm
      .filter((lang): lang is string => typeof lang === 'string' && lang.trim().length > 0)
      .map(lang => lang);
    if (normalized.length > 0) {
      return normalized;
    }
    return basicInfoAvailableLanguages.length > 0 ? [...basicInfoAvailableLanguages] : ['ko'];
  }, [basicInfoAvailableLanguages, formMethods]);

  const persistCurrentLanguageState = useCallback(() => {
    if (!currentCourseId) {
      return;
    }
    const snapshot = getSupportedLanguagesSnapshot();
    persistLanguageState(currentCourseId, {
      currentLanguage: currentBasicInfoLanguageRef.current,
      supportedLanguages: snapshot.length > 0 ? snapshot : ['ko'],
    });
  }, [currentCourseId, getSupportedLanguagesSnapshot]);

  useEffect(() => {
    const languagesFromTranslations = Object.keys(basicInfoTranslationsRef.current);
    if (languagesFromTranslations.length === 0) {
      return;
    }
    formMethods.setValue('supportedLanguages', languagesFromTranslations, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [basicInfoTranslationsVersion, formMethods]);

  const defaultLanguageRef = useRef<string>('ko');
  const currentBasicInfoLanguageRef = useRef<string>('ko');
  const basicInfoTranslationsRef = useRef<Record<string, BasicInfoTranslationEntry>>({
    ko: { title: '제목 없음', description: '' },
  });

  const persistTranslationsSnapshot = useCallback(() => {
    if (!currentCourseId) {
      return;
    }
    const snapshot = Object.entries(basicInfoTranslationsRef.current).reduce<PersistedTranslationMap>((acc, [languageCode, entry]) => {
      const normalized = normalizeLanguageCode(languageCode);
      if (!normalized) {
        return acc;
      }
      acc[normalized] = {
        title: sanitizeTitle(entry.title),
        description: entry.description ?? '',
      };
      return acc;
    }, {} as PersistedTranslationMap);
    persistLanguageTranslations(currentCourseId, snapshot);
  }, [currentCourseId, sanitizeTitle]);

  const convertSectionsToDraft = useCallback((sections: SectionData[]): any[] => (
    (sections || []).map((section, sectionIndex) => ({
      id: section.id,
      title: section.title ?? '',
      order: section.order ?? sectionIndex + 1,
      lectures: (section.lessons || []).map((lesson, lessonIndex) => ({
        id: lesson.id,
        title: lesson.title ?? '',
        order: lesson.order ?? lessonIndex + 1,
        contentType: (lesson.contentType ?? 'Markdown').toString().toUpperCase(),
        platformSupport: lesson.platformSupport ?? 'IdeAndWeb',
        duration: lesson.duration ?? null,
        markdownUrl: lesson.markdownUrl,
        videoUrl: lesson.videoUrl,
      })),
    }))
  ), []);

  const bumpBasicInfoTranslationsVersion = useCallback(() => {
    setBasicInfoTranslationsVersion(prev => prev + 1);
  }, []);

  const applyLanguageToForm = useCallback((languageCode: string) => {
    const normalized = normalizeLanguageCode(languageCode);
    if (!normalized) {
      return;
    }
    const entry = basicInfoTranslationsRef.current[normalized] ?? { title: '', description: '' };
    formMethods.setValue('title', sanitizeTitle(entry.title), { shouldDirty: false, shouldTouch: false });
    formMethods.setValue('description', entry.description ?? '', { shouldDirty: false, shouldTouch: false });
  }, [formMethods]);

  useEffect(() => {
    if (!currentCourseId) {
      return;
    }
    const persisted = loadPersistedLanguageState(currentCourseId);
    if (!persisted) {
      return;
    }

    const normalizedSupported = Array.from(
      new Set(
        (persisted.supportedLanguages ?? [])
          .map(code => normalizeLanguageCode(code))
          .filter((code): code is string => Boolean(code)),
      ),
    );

    if (normalizedSupported.length > 0) {
      formMethods.setValue('supportedLanguages', normalizedSupported, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
      setBasicInfoAvailableLanguages(normalizedSupported);
    }

    const restoredLanguage = normalizeLanguageCode(persisted.currentLanguage) ?? normalizedSupported[0] ?? null;
    if (restoredLanguage) {
      currentBasicInfoLanguageRef.current = restoredLanguage;
      setCurrentBasicInfoLanguage(restoredLanguage);
      applyLanguageToForm(restoredLanguage);
    }
  }, [applyLanguageToForm, currentCourseId, formMethods]);

  const syncCurrentBasicInfo = useCallback(() => {
    const activeLanguage = currentBasicInfoLanguageRef.current;
    const currentTitle = sanitizeTitle(formMethods.getValues('title'));
    const currentDescription = formMethods.getValues('description') ?? '';
    basicInfoTranslationsRef.current[activeLanguage] = {
      title: currentTitle,
      description: currentDescription,
    };
    persistTranslationsSnapshot();
  }, [formMethods, persistTranslationsSnapshot, sanitizeTitle]);

  const changeBasicInfoLanguage = useCallback((languageCode: string) => {
    const normalized = normalizeLanguageCode(languageCode);
    if (!normalized || normalized === currentBasicInfoLanguageRef.current) {
      return;
    }

    syncCurrentBasicInfo();

    if (!basicInfoTranslationsRef.current[normalized]) {
      basicInfoTranslationsRef.current[normalized] = { title: '', description: '' };
    }

    currentBasicInfoLanguageRef.current = normalized;
    setCurrentBasicInfoLanguage(normalized);
    applyLanguageToForm(normalized);
    bumpBasicInfoTranslationsVersion();
    persistCurrentLanguageState();
    persistTranslationsSnapshot();

    if (currentCourseId && currentCourseId !== 'new') {
      const store = useCurriculumStore.getState();
      const snapshots = store.getLanguageSnapshots(currentCourseId);
      const snapshotEntry = snapshots[normalized];
      const fallbackSections = snapshotEntry?.sections
        ? cloneSectionsShallow(snapshotEntry.sections)
        : cloneSectionsShallow(lastSavedCurriculumSnapshotsRef.current[normalized] ?? []);

      store.switchLanguage(normalized, {
        courseId: currentCourseId,
        fallbackSections,
        providedTimestamp: snapshotEntry?.lastModifiedLocallyTimestamp ?? null,
      });
    }
  }, [
    applyLanguageToForm,
    bumpBasicInfoTranslationsVersion,
    currentCourseId,
    persistCurrentLanguageState,
    persistTranslationsSnapshot,
    syncCurrentBasicInfo,
  ]);

  const copyBasicInfoFromDefault = useCallback(() => {
    const defaultLanguage = defaultLanguageRef.current;
    const activeLanguage = currentBasicInfoLanguageRef.current;

    if (defaultLanguage === activeLanguage) {
      return;
    }

    syncCurrentBasicInfo();

    const source = basicInfoTranslationsRef.current[defaultLanguage] ?? { title: '', description: '' };
    basicInfoTranslationsRef.current[activeLanguage] = {
      title: source.title ?? '',
      description: source.description ?? '',
    };

    formMethods.setValue('title', source.title ?? '', { shouldDirty: true, shouldTouch: true });
    formMethods.setValue('description', source.description ?? '', { shouldDirty: true, shouldTouch: true });
    bumpBasicInfoTranslationsVersion();
    persistTranslationsSnapshot();
  }, [bumpBasicInfoTranslationsVersion, formMethods, persistTranslationsSnapshot, syncCurrentBasicInfo]);

  const persistAutosaveSnapshot = useCallback(
    (courseId: string, formData?: BasicInfoFormData | null) => {
      if (!courseId || courseId === 'new') {
        return false;
      }

      syncCurrentBasicInfo();

    const currentFormData = formData ?? formMethods.getValues();
    const sanitizedTitle = sanitizeTitle(currentFormData.title);
    const snapshot = JSON.stringify({
      title: sanitizedTitle,
      description: currentFormData.description ?? '',
      level: currentFormData.level ?? '',
      isPublic: currentFormData.isPublic ?? true,
      allowComments: currentFormData.allowComments ?? true,
      defaultLanguage: currentFormData.defaultLanguage ?? 'ko',
      supportedLanguages: currentFormData.supportedLanguages ?? ['ko'],
      priceAmount: typeof currentFormData.priceAmount === 'number'
        ? currentFormData.priceAmount
        : Number(currentFormData.priceAmount ?? 0),
      priceCurrency: currentFormData.priceCurrency ?? 'KRW',
      isFree: currentFormData.isFree ?? ((currentFormData.priceAmount ?? 0) <= 0),
    });

      if (lastAutosavedSnapshotRef.current === snapshot) {
        return false;
      }

      lastAutosavedSnapshotRef.current = snapshot;

      const autosaveData = {
        basicInfo: {
          ...currentFormData,
          title: sanitizedTitle,
        },
        currentLanguage: currentBasicInfoLanguageRef.current,
        translations: basicInfoTranslationsRef.current,
      };

      saveAutosaveData(courseId, autosaveData);
      persistTranslationsSnapshot();

      if (handledAutosavePrompts.has(courseId)) {
        const latestEntry = loadAutosaveEntry(courseId);
        if (latestEntry) {
          handledAutosavePrompts.set(courseId, latestEntry.timestamp);
        }
      }
      return true;
    },
    [formMethods, persistTranslationsSnapshot, sanitizeTitle, syncCurrentBasicInfo],
  );

  const debouncedAutosave = useMemo(
    () => debounce((courseId: string, formData: BasicInfoFormData) => {
      persistAutosaveSnapshot(courseId, formData);
    }, 1000),
    [persistAutosaveSnapshot],
  );

  const flushAutosaveSnapshot = useCallback(() => {
    if (!currentCourseId) {
      return false;
    }
    const currentFormData = formMethods.getValues();
    return persistAutosaveSnapshot(currentCourseId, currentFormData);
  }, [currentCourseId, formMethods, persistAutosaveSnapshot]);

  const syncBasicInfoLanguageMetadata = useCallback((languages: string[], defaultLanguage?: string | null) => {
    const normalizedDefault = normalizeLanguageCode(defaultLanguage) ?? defaultLanguageRef.current ?? 'ko';
    const normalizedLanguages = Array.from(
      new Set(
        (languages || [])
          .map(code => normalizeLanguageCode(code))
          .filter((code): code is string => Boolean(code)),
      ),
    );

    Object.keys(basicInfoTranslationsRef.current).forEach(existingLang => {
      const normalized = normalizeLanguageCode(existingLang);
      if (normalized && !normalizedLanguages.includes(normalized)) {
        normalizedLanguages.push(normalized);
      }
    });

    const currentSnapshot = getSupportedLanguagesSnapshot().map(code => normalizeLanguageCode(code) ?? code);
    const normalizedSnapshotSet = new Set(currentSnapshot);
    const normalizedIncomingSet = new Set(normalizedLanguages);
    const isSame = normalizedSnapshotSet.size === normalizedIncomingSet.size
      && Array.from(normalizedIncomingSet).every(lang => normalizedSnapshotSet.has(lang));

    if (isSame) {
      return;
    }

    defaultLanguageRef.current = normalizedDefault;

    if (!normalizedLanguages.includes(normalizedDefault)) {
      normalizedLanguages.push(normalizedDefault);
    }

    let mutated = false;

    normalizedLanguages.forEach(lang => {
      if (!basicInfoTranslationsRef.current[lang]) {
        basicInfoTranslationsRef.current[lang] = { title: '', description: '' };
        mutated = true;
      }
    });

    if (!normalizedLanguages.includes(currentBasicInfoLanguageRef.current)) {
      const fallback = normalizedDefault || normalizedLanguages[0] || 'ko';
      currentBasicInfoLanguageRef.current = fallback;
      setCurrentBasicInfoLanguage(fallback);
      applyLanguageToForm(fallback);
      mutated = true;
      persistCurrentLanguageState();
    }

    const normalizedLanguagesOrFallback = normalizedLanguages.length > 0 ? normalizedLanguages : ['ko'];
    setBasicInfoAvailableLanguages(normalizedLanguagesOrFallback);
    formMethods.setValue('supportedLanguages', normalizedLanguagesOrFallback, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
    persistCurrentLanguageState();
    persistTranslationsSnapshot();

    if (mutated) {
      bumpBasicInfoTranslationsVersion();
    }
  }, [
    applyLanguageToForm,
    bumpBasicInfoTranslationsVersion,
    formMethods,
    getSupportedLanguagesSnapshot,
    persistCurrentLanguageState,
    persistTranslationsSnapshot,
  ]);

  const basicInfoTranslationSummary = useMemo<BasicInfoTranslationSummary>(() => {
    const summary: BasicInfoTranslationSummary = {};
    const map = basicInfoTranslationsRef.current;
    Object.entries(map).forEach(([lang, { title, description }]) => {
      summary[lang] = {
        isTitleMissing: !(title ?? '').trim(),
        isDescriptionMissing: !(description ?? '').trim(),
      };
    });
    return summary;
  }, [basicInfoTranslationsVersion]);

  const mapApiToSections = useCallback((data: CourseDraftDetailResponse): SectionData[] => {
    const sections = data.sections || [];
    const mapType = (contentType?: string): SectionData['lessons'][number]['type'] => {
      switch (contentType) {
        case 'VIDEO':
        case 'VIDEO_MARKDOWN':
          return 'video';
        case 'QUIZ':
          return 'quiz';
        case 'TEXT':
        case 'MARKDOWN':
        default:
          return 'text';
      }
    };
    return sections.map((s, sidx) => ({
      id: String((s as any).id ?? `s-${sidx}`),
      title: (s as any).title ?? '',
      lessons: ((s as any).lectures || []).map((l: any, lidx: number) => ({
        id: String(l.id ?? `l-${sidx}-${lidx}`),
        title: l.title ?? '',
        type: mapType(l.contentType),
        duration: typeof l.duration === 'number' ? l.duration : (Number(l.duration) || undefined),
      })),
    }));
  }, []);

  // 폼 변경 감지 및 자동 저장
  useEffect(() => {
    if (!currentCourseId || currentCourseId === 'new') {
      return;
    }

    // 초기화 단계에서 불필요한 저장 방지
    if (!hasInitializedAutosaveRef.current) {
      hasInitializedAutosaveRef.current = true;
      const initialFormData = formMethods.getValues();
      lastAutosavedSnapshotRef.current = JSON.stringify({
        title: initialFormData.title ?? '',
        description: initialFormData.description ?? '',
        level: initialFormData.level ?? '',
        isPublic: initialFormData.isPublic ?? true,
        allowComments: initialFormData.allowComments ?? true,
        defaultLanguage: initialFormData.defaultLanguage ?? 'ko',
        supportedLanguages: initialFormData.supportedLanguages ?? ['ko'],
        priceAmount: typeof initialFormData.priceAmount === 'number'
          ? initialFormData.priceAmount
          : Number(initialFormData.priceAmount ?? 0),
        priceCurrency: initialFormData.priceCurrency ?? 'KRW',
        isFree: initialFormData.isFree ?? ((initialFormData.priceAmount ?? 0) <= 0),
      });
      return;
    }

    const currentFormData = formMethods.getValues();
    const sanitizedTitle = sanitizeTitle(currentFormData.title);
    const snapshot = JSON.stringify({
      title: sanitizedTitle,
      description: currentFormData.description ?? '',
      level: currentFormData.level ?? '',
      isPublic: currentFormData.isPublic ?? true,
      allowComments: currentFormData.allowComments ?? true,
      defaultLanguage: currentFormData.defaultLanguage ?? 'ko',
      supportedLanguages: currentFormData.supportedLanguages ?? ['ko'],
    });

    if (lastAutosavedSnapshotRef.current === snapshot) {
      return;
    }

    syncCurrentBasicInfo();
    debouncedAutosave(currentCourseId, currentFormData);
    setHasUnsavedChanges(true);
  }, [currentCourseId, debouncedAutosave, formMethods, syncCurrentBasicInfo, watchedBasicInfo]);

  // 코스 상세 로드 (편집 시) - React Query 데이터 직접 사용
  const processedCourseSignatureRef = useRef<string | null>(null);

  // 임시저장 데이터 복원
  const restoreAutosaveData = useCallback((courseId: string) => {
    if (!courseId || courseId === 'new') {
      return false;
    }

    const autosaveEntry = loadAutosaveEntry(courseId);
    if (!autosaveEntry) {
      return false;
    }

    const autosaveData = autosaveEntry.data;

    try {
      const { basicInfo, currentLanguage, translations } = autosaveData;

      // 기본 정보 복원
      if (basicInfo) {
        Object.keys(basicInfo).forEach(key => {
          let value = basicInfo[key];
          if (key === 'title') {
            value = sanitizeTitle(value);
          }
          if (value !== undefined) {
            formMethods.setValue(key as any, value, { shouldDirty: true, shouldTouch: true });
          }
        });
      }

      // 번역 데이터 복원
      if (translations) {
        basicInfoTranslationsRef.current = translations;
        bumpBasicInfoTranslationsVersion();
        persistTranslationsSnapshot();
      }

      // 현재 언어 복원
      if (currentLanguage) {
        currentBasicInfoLanguageRef.current = currentLanguage;
        setCurrentBasicInfoLanguage(currentLanguage);
        applyLanguageToForm(currentLanguage);
      }

      const snapshot = JSON.stringify({
        title: formMethods.getValues('title') ?? '',
        description: formMethods.getValues('description') ?? '',
        level: formMethods.getValues('level') ?? '',
        isPublic: formMethods.getValues('isPublic') ?? true,
        allowComments: formMethods.getValues('allowComments') ?? true,
        defaultLanguage: formMethods.getValues('defaultLanguage') ?? 'ko',
        supportedLanguages: formMethods.getValues('supportedLanguages') ?? ['ko'],
        priceAmount: typeof formMethods.getValues('priceAmount') === 'number'
          ? formMethods.getValues('priceAmount')
          : Number(formMethods.getValues('priceAmount') ?? 0),
        priceCurrency: formMethods.getValues('priceCurrency') ?? 'KRW',
        isFree: formMethods.getValues('isFree') ?? ((formMethods.getValues('priceAmount') ?? 0) <= 0),
      });
      lastAutosavedSnapshotRef.current = snapshot;
      autosaveRestoredRef.current = true;
      return true;
    } catch (error) {
      console.error('[Autosave] 복원 실패:', error);
      return false;
    }
  }, [
    formMethods,
    bumpBasicInfoTranslationsVersion,
    setCurrentBasicInfoLanguage,
    applyLanguageToForm,
    persistTranslationsSnapshot,
  ]);

  const applyCourseData = useCallback((
    loaded: CourseDraftDetailResponse | undefined,
    options?: { force?: boolean },
  ) => {
    if (!loaded) {
      return;
    }

    const signature = JSON.stringify({
      id: loaded.id,
      updatedAt: (loaded as any)?.updatedAt ?? (loaded as any)?.lastModifiedAt ?? null,
      title: loaded.title,
      defaultLanguage: (loaded as any)?.defaultLanguage ?? null,
      supportedLanguages: (loaded as any)?.supportedLanguages ?? [],
      localizedBasicInfo: (loaded as any)?.localizedBasicInfo ?? null,
      sectionsCount: (loaded.sections || []).length,
      localizedSectionsKeys: Object.keys((loaded as any)?.localizedSections ?? {}).length,
    });

    if (!options?.force && processedCourseSignatureRef.current === signature) {
      return;
    }
    processedCourseSignatureRef.current = signature;

    // 임시저장 복원 완료 후에는 서버 데이터로 덮어쓰지 않음
    if (autosaveRestoredRef.current) {
      return;
    }

    // 임시저장 데이터 확인 및 모달 표시
    const courseIdForAutosave = loaded.id || currentCourseId;
    if (courseIdForAutosave) {
      let shouldSkipAutosavePrompt = false;
      try {
        const skipKey = `course_edit_skip_autosave_prompt_${courseIdForAutosave}`;
        if (sessionStorage.getItem(skipKey) === '1') {
          shouldSkipAutosavePrompt = true;
          sessionStorage.removeItem(skipKey);
        }
      } catch (error) {
        // sessionStorage를 사용할 수 없는 환경에서는 자동 건너뛰기 기능을 무시
      }

      const autosaveEntry = loadAutosaveEntry(courseIdForAutosave);
      if (autosaveEntry && !shouldSkipAutosavePrompt) {
        const handledTimestamp = handledAutosavePrompts.get(courseIdForAutosave);
        if (handledTimestamp !== autosaveEntry.timestamp) {
          pendingAutosaveTimestampRef.current = autosaveEntry.timestamp ?? null;
          setAutosaveModalState({
            isOpen: true,
            courseId: courseIdForAutosave,
          });
        } else {
          pendingAutosaveTimestampRef.current = null;
        }
      } else {
        pendingAutosaveTimestampRef.current = null;
      }
    } else {
      pendingAutosaveTimestampRef.current = null;
    }

    const serverUpdatedRaw = (loaded as any)?.updatedAt ?? (loaded as any)?.lastModifiedAt ?? null;
    const serverLastUpdatedTs = typeof serverUpdatedRaw === 'string'
      ? (() => {
          const parsed = Date.parse(serverUpdatedRaw);
          return Number.isNaN(parsed) ? null : parsed;
        })()
      : typeof serverUpdatedRaw === 'number'
        ? serverUpdatedRaw
        : null;

    const rawSupported: string[] = Array.isArray((loaded as any)?.supportedLanguages)
      ? (loaded as any).supportedLanguages.filter((code: unknown): code is string => typeof code === 'string' && code.trim().length > 0)
      : [];

    const persistedLanguageState = loadPersistedLanguageState(currentCourseId ?? (loaded as any)?.id ?? null);
    const persistedTranslations = loadPersistedLanguageTranslations(currentCourseId ?? (loaded as any)?.id ?? null);
    const normalizedSupportedSet = new Set<string>();
    const pushLanguage = (value?: string | null) => {
      const normalized = normalizeLanguageCode(value);
      if (normalized) {
        normalizedSupportedSet.add(normalized);
      }
    };

    rawSupported.forEach(pushLanguage);
    persistedLanguageState?.supportedLanguages?.forEach(pushLanguage);
    Object.keys(basicInfoTranslationsRef.current ?? {}).forEach(pushLanguage);

    if (normalizedSupportedSet.size === 0) {
      normalizedSupportedSet.add('ko');
    }

    const normalizedSupportedOriginal = Array.from(normalizedSupportedSet);

    const incomingDefault = (loaded as any)?.defaultLanguage as string | undefined;
    const normalizedIncomingDefault = normalizeLanguageCode(incomingDefault);
    const normalizedDefaultOriginal = normalizedIncomingDefault && normalizedSupportedSet.has(normalizedIncomingDefault)
      ? normalizedIncomingDefault
      : normalizedSupportedOriginal[0];

    const normalizedPersistedCurrent = normalizeLanguageCode(persistedLanguageState?.currentLanguage);
    const editingLanguage = normalizedPersistedCurrent && normalizedSupportedSet.has(normalizedPersistedCurrent)
      ? normalizedPersistedCurrent
      : normalizedDefaultOriginal;

    const sanitizedTitleForReset = sanitizeTitle(loaded.title);

    const loadedPrice = (loaded as any)?.price;
    const rawLoadedPrice = typeof loadedPrice?.amount === 'string'
      ? Number(loadedPrice.amount)
      : loadedPrice?.amount;
    const normalizedLoadedPrice = Number.isFinite(rawLoadedPrice) ? Math.max(0, rawLoadedPrice as number) : 0;
    const loadedPriceCurrency = typeof loadedPrice?.currency === 'string' && loadedPrice.currency.trim().length > 0
      ? loadedPrice.currency
      : 'KRW';
    const isFreeFromLoaded = normalizedLoadedPrice <= 0;

    reset({
      title: sanitizedTitleForReset,
      description: loaded.description || '',
      level: loaded.level || '',
      thumbnailUrl: loaded.thumbnailUrl || undefined,
      isPublic: (loaded as any)?.isPublic ?? true,
      allowComments: (loaded as any)?.allowComments ?? true,
      defaultLanguage: normalizedDefaultOriginal,
      supportedLanguages: normalizedSupportedOriginal,
      draftVersion: (loaded as any)?.draftVersion ?? null,
      priceAmount: normalizedLoadedPrice,
      priceCurrency: loadedPriceCurrency,
      isFree: isFreeFromLoaded,
    });

    lastAutosavedSnapshotRef.current = JSON.stringify({
      title: sanitizedTitleForReset,
      description: loaded.description ?? '',
      level: loaded.level ?? '',
      isPublic: (loaded as any)?.isPublic ?? true,
      allowComments: (loaded as any)?.allowComments ?? true,
      defaultLanguage: normalizedDefaultOriginal,
      supportedLanguages: normalizedSupportedOriginal,
      priceAmount: normalizedLoadedPrice,
      priceCurrency: loadedPriceCurrency,
      isFree: isFreeFromLoaded,
    });

    coreLogic.actions.setThumbnailPreview(loaded.thumbnailUrl ?? '');

    const normalizedDefault = normalizeLanguageCode(normalizedDefaultOriginal) ?? 'ko';
    const normalizedSupported = normalizedSupportedOriginal
      .map(code => normalizeLanguageCode(code))
      .filter((code): code is string => Boolean(code));

    defaultLanguageRef.current = normalizedDefault;
    currentBasicInfoLanguageRef.current = editingLanguage;
    setCurrentBasicInfoLanguage(editingLanguage);

    const translationMap: Record<string, BasicInfoTranslationEntry> = {};
    const localizedBasicInfo = (loaded as any)?.localizedBasicInfo as Record<string, BasicInfoTranslationEntry> | undefined;

    if (localizedBasicInfo && typeof localizedBasicInfo === 'object') {
      Object.entries(localizedBasicInfo).forEach(([languageCode, entry]) => {
        const normalizedLang = normalizeLanguageCode(languageCode);
        if (!normalizedLang) {
          return;
        }
        translationMap[normalizedLang] = {
          title: entry?.title ?? '',
          description: entry?.description ?? '',
        };
      });
    }

    translationMap[normalizedDefault] = {
      title: sanitizedTitleForReset,
      description: loaded.description ?? '',
    };

    const previousTranslations = basicInfoTranslationsRef.current ?? {};
    normalizedSupported.forEach(lang => {
      if (!translationMap[lang]) {
        const previous = previousTranslations[lang];
        translationMap[lang] = previous
          ? {
              title: sanitizeTitle(previous.title),
              description: previous.description ?? '',
            }
          : { title: '', description: '' };
      }
    });

    if (persistedTranslations && typeof persistedTranslations === 'object') {
      Object.entries(persistedTranslations).forEach(([languageCode, entry]) => {
        const normalizedLang = normalizeLanguageCode(languageCode);
        if (!normalizedLang) {
          return;
        }
        const persistedTitle = sanitizeTitle(entry?.title ?? '');
        const persistedDescription = entry?.description ?? '';
        const current = translationMap[normalizedLang];
        const hasCurrentContent = Boolean((current?.title ?? '').trim().length > 0 || (current?.description ?? '').trim().length > 0);
        const hasPersistedContent = Boolean((persistedTitle ?? '').trim().length > 0 || (persistedDescription ?? '').trim().length > 0);
        if (!current || (!hasCurrentContent && hasPersistedContent)) {
          translationMap[normalizedLang] = {
            title: persistedTitle,
            description: persistedDescription,
          };
        }
      });
    }

    basicInfoTranslationsRef.current = translationMap;
    persistTranslationsSnapshot();
    applyLanguageToForm(editingLanguage);
    setBasicInfoAvailableLanguages(
      normalizedSupported.length > 0
        ? Array.from(new Set([...normalizedSupported, normalizedDefault]))
        : [normalizedDefault],
    );
    bumpBasicInfoTranslationsVersion();

    const savedLocalizedBasicInfo: Record<string, { title: string; description?: string | null }> = {};
    Object.entries(translationMap).forEach(([lang, entry]) => {
      savedLocalizedBasicInfo[lang] = {
        title: entry?.title ?? '',
        description: entry?.description ?? '',
      };
    });

    lastSavedBasicInfoRef.current = {
      title: translationMap[normalizedDefault]?.title ?? sanitizedTitleForReset,
      description: translationMap[normalizedDefault]?.description ?? loaded.description ?? '',
      level: ((loaded as any)?.level as string | undefined) ?? '',
      thumbnailUrl: loaded.thumbnailUrl ?? undefined,
      isPublic: (loaded as any)?.isPublic ?? true,
      allowComments: (loaded as any)?.allowComments ?? true,
      defaultLanguage: normalizedDefaultOriginal,
      supportedLanguages: normalizedSupportedOriginal,
      localizedBasicInfo: savedLocalizedBasicInfo,
      activeBasicInfoLanguage: currentBasicInfoLanguageRef.current,
      draftVersion: (loaded as any)?.draftVersion ?? null,
      priceAmount: normalizedLoadedPrice,
      priceCurrency: loadedPriceCurrency,
      isFree: isFreeFromLoaded,
    };

    let mappedDefaultSections: SectionData[] = [];
    let savedSnapshots: Record<string, SectionData[]> = {};

    try {
      mappedDefaultSections = mapApiToSections(loaded as CourseDraftDetailResponse);
      setInitialSections(mappedDefaultSections);

      const localizedSections = (loaded as any)?.localizedSections as Record<string, unknown> | undefined;
      if (localizedSections && typeof localizedSections === 'object') {
        const localizedMap: Record<string, SectionData[]> = {};
        Object.entries(localizedSections).forEach(([languageCode, rawSections]) => {
          if (!Array.isArray(rawSections)) {
            return;
          }
          const normalizedLang = normalizeLanguageCode(languageCode) ?? languageCode;
          const pseudoData = {
            ...loaded,
            sections: rawSections,
          } as CourseDraftDetailResponse;
          try {
            localizedMap[normalizedLang] = mapApiToSections(pseudoData);
          } catch (error) {
            console.warn('[useCourseEditLogic] 로컬라이즈드 섹션 매핑 실패:', normalizedLang, error);
          }
        });

        const normalizedSupportedFallback = normalizedSupported[0];
        const finalDefaultKey = normalizedDefault ?? normalizedSupportedFallback ?? 'ko';
        localizedMap[finalDefaultKey] = mappedDefaultSections;

        setInitialLocalizedSections(localizedMap);
        if (options?.force) {
          Object.entries(localizedMap).forEach(([languageCode, sections]) => {
            const normalizedLang = normalizeLanguageCode(languageCode) ?? languageCode;
            primeLanguageSnapshot(loaded.id ?? currentCourseId ?? '', normalizedLang, {
              sections,
              isCurriculumDirty: false,
              lastModifiedLocallyTimestamp: serverLastUpdatedTs ?? Date.now(),
            });
          });
        }
        savedSnapshots = Object.entries(localizedMap).reduce<Record<string, SectionData[]>>((acc, [languageCode, sections]) => {
          const normalizedLang = normalizeLanguageCode(languageCode) ?? languageCode;
          acc[normalizedLang] = cloneSectionsShallow(sections);
          return acc;
        }, {});
      } else {
        setInitialLocalizedSections(undefined);
      }
    } catch (error) {
      console.warn('[useCourseEditLogic] 섹션 매핑 중 오류', error);
      setInitialSections([]);
      setInitialLocalizedSections(undefined);
    }

    syncBasicInfoLanguageMetadata(normalizedSupportedOriginal, normalizedDefaultOriginal);

    if (options?.force) {
      resetCurriculumState();
      initializeCurriculumSections(mappedDefaultSections, currentCourseId ?? '', {
        providedTimestamp: serverLastUpdatedTs ?? null,
        language: normalizedDefault,
      });
      const targetLanguage = normalizedDefault ?? normalizedSupportedOriginal[0] ?? 'ko';
      primeLanguageSnapshot(loaded.id ?? currentCourseId ?? '', targetLanguage, {
        sections: mappedDefaultSections,
        isCurriculumDirty: false,
        lastModifiedLocallyTimestamp: serverLastUpdatedTs ?? Date.now(),
      });
      setCurriculumDirty(false);
      setResetSignal(prev => prev + 1);
    }

    if (Object.keys(savedSnapshots).length === 0) {
      savedSnapshots = {
        [normalizedDefault]: cloneSectionsShallow(mappedDefaultSections),
      };
    } else if (!savedSnapshots[normalizedDefault]) {
      savedSnapshots[normalizedDefault] = cloneSectionsShallow(mappedDefaultSections);
    }

    lastSavedCurriculumSnapshotsRef.current = savedSnapshots;
    persistCurriculumSnapshots(currentCourseId, savedSnapshots);
    setCurriculumDirty(false);
    setHasUnsavedChanges(false);
  }, [
    applyLanguageToForm,
    bumpBasicInfoTranslationsVersion,
    coreLogic.actions.setThumbnailPreview,
    mapApiToSections,
    reset,
    setBasicInfoAvailableLanguages,
    setCurriculumDirty,
    setCurrentBasicInfoLanguage,
    setInitialLocalizedSections,
    setInitialSections,
    syncBasicInfoLanguageMetadata,
    primeLanguageSnapshot,
    resetCurriculumState,
    initializeCurriculumSections,
    restoreAutosaveData,
    persistTranslationsSnapshot,
    persistCurriculumSnapshots,
    setHasUnsavedChanges,
    currentCourseId,
  ]);

  useEffect(() => {
    applyCourseData(coreLogic.courseData as CourseDraftDetailResponse | undefined);
  }, [applyCourseData, coreLogic.courseData]);

  // 페이지 언로드 시 경고
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (skipNextBeforeUnloadRef.current) {
        skipNextBeforeUnloadRef.current = false;
        return;
      }
      if (hasUnsavedChanges && currentCourseId && currentCourseId !== 'new') {
        const message = '저장하지 않은 변경사항이 있습니다. 페이지를 나가시겠습니까?';
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, currentCourseId]);

  const bypassNextBeforeUnload = useCallback(() => {
    skipNextBeforeUnloadRef.current = true;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const globalObj = window as typeof window & {
      __courseEditTesting?: Record<string, unknown>;
    };

    const resetBasicInfoForTesting = (payload?: Partial<BasicInfoFormData>) => {
      const currentValues = formMethods.getValues();
      formMethods.reset(
        { ...currentValues, ...(payload ?? {}) },
        { keepDirty: false, keepDefaultValues: false },
      );
      setCurriculumDirty(false);
      setHasUnsavedChanges(false);
    };

    const setPriceForTesting = (amount?: number | null, currency?: string | null, isFreeOverride?: boolean | null) => {
      const rawAmount = typeof amount === 'number'
        ? amount
        : Number(amount ?? 0);
      const normalizedAmount = Number.isFinite(rawAmount) ? Math.max(0, rawAmount) : 0;
      const normalizedCurrency = currency?.toString().trim().toUpperCase() || 'KRW';
      const resolvedIsFree = isFreeOverride ?? normalizedAmount <= 0;

      formMethods.setValue('priceAmount', normalizedAmount, { shouldDirty: true, shouldTouch: true });
      formMethods.setValue('priceCurrency', normalizedCurrency, { shouldDirty: true, shouldTouch: true });
      formMethods.setValue('isFree', resolvedIsFree, { shouldDirty: true, shouldTouch: true });
    };

    const openAutosaveModalForTesting = () => {
      if (!currentCourseId || currentCourseId === 'new') {
        return false;
      }
      if (!hasAutosaveData(currentCourseId)) {
        return false;
      }
      setAutosaveModalState({ isOpen: true, courseId: currentCourseId });
      return true;
    };

    globalObj.__courseEditTesting = {
      ...(globalObj.__courseEditTesting ?? {}),
      resetBasicInfoForTesting,
      openAutosaveModalForTesting,
      setPriceForTesting,
      getCurrentLanguageForTesting: () => currentBasicInfoLanguageRef.current,
      getSupportedLanguagesForTesting: () => Array.from(basicInfoAvailableLanguages),
      getBasicInfoTranslationForTesting: (language?: string | null) => {
        const normalized = normalizeLanguageCode(language) ?? currentBasicInfoLanguageRef.current;
        const entry = normalized ? basicInfoTranslationsRef.current[normalized] : undefined;
        if (!entry) {
          return null;
        }
        return {
          title: entry.title ?? '',
          description: entry.description ?? '',
        };
      },
      getHasUnsavedChangesForTesting: () => hasUnsavedChanges,
      getCurriculumDirtyForTesting: () => useCurriculumStore.getState().isCurriculumDirty,
      getFormDirtyStateForTesting: () => formMethods.formState.isDirty,
      getSavingStateForTesting: () => Boolean(coreLogic.state.isSaving),
      getPublishingStateForTesting: () => Boolean(coreLogic.state.isPublishing),
      getPublishedStateForTesting: () => Boolean((coreLogic.courseData as any)?.isPublished ?? false),
      getCurriculumLanguageForTesting: () => normalizeLanguageCode(useCurriculumStore.getState().currentLanguage) ?? null,
    };

    return () => {
      if (globalObj.__courseEditTesting?.resetBasicInfoForTesting === resetBasicInfoForTesting) {
        delete globalObj.__courseEditTesting.resetBasicInfoForTesting;
      }
      if (globalObj.__courseEditTesting?.openAutosaveModalForTesting === openAutosaveModalForTesting) {
        delete globalObj.__courseEditTesting.openAutosaveModalForTesting;
      }
      if (globalObj.__courseEditTesting?.setPriceForTesting === setPriceForTesting) {
        delete globalObj.__courseEditTesting.setPriceForTesting;
      }
      if (globalObj.__courseEditTesting?.getCurrentLanguageForTesting) {
        delete globalObj.__courseEditTesting.getCurrentLanguageForTesting;
      }
      if (globalObj.__courseEditTesting?.getSupportedLanguagesForTesting) {
        delete globalObj.__courseEditTesting.getSupportedLanguagesForTesting;
      }
      if (globalObj.__courseEditTesting?.getHasUnsavedChangesForTesting) {
        delete globalObj.__courseEditTesting.getHasUnsavedChangesForTesting;
      }
      if (globalObj.__courseEditTesting?.getBasicInfoTranslationForTesting) {
        delete globalObj.__courseEditTesting.getBasicInfoTranslationForTesting;
      }
      if (globalObj.__courseEditTesting?.getCurriculumDirtyForTesting) {
        delete globalObj.__courseEditTesting.getCurriculumDirtyForTesting;
      }
      if (globalObj.__courseEditTesting?.getFormDirtyStateForTesting) {
        delete globalObj.__courseEditTesting.getFormDirtyStateForTesting;
      }
      if (globalObj.__courseEditTesting?.getSavingStateForTesting) {
        delete globalObj.__courseEditTesting.getSavingStateForTesting;
      }
      if (globalObj.__courseEditTesting?.getPublishingStateForTesting) {
        delete globalObj.__courseEditTesting.getPublishingStateForTesting;
      }
      if (globalObj.__courseEditTesting?.getPublishedStateForTesting) {
        delete globalObj.__courseEditTesting.getPublishedStateForTesting;
      }
      if (globalObj.__courseEditTesting?.getCurriculumLanguageForTesting) {
        delete globalObj.__courseEditTesting.getCurriculumLanguageForTesting;
      }
    };
  }, [basicInfoAvailableLanguages, currentCourseId, formMethods, setCurriculumDirty]);

  const captureCurrentStateAsSaved = useCallback(() => {
    if (!currentCourseId) {
      return;
    }

    const supportedLanguagesCurrent = formMethods.getValues('supportedLanguages') ?? [];
    const defaultLanguageCurrent = formMethods.getValues('defaultLanguage') ?? defaultLanguageRef.current ?? 'ko';
    const normalizedDefault = normalizeLanguageCode(defaultLanguageCurrent) ?? 'ko';

    const savedLocalizedBasicInfo: Record<string, { title: string; description?: string | null }> = {};
    Object.entries(basicInfoTranslationsRef.current).forEach(([lang, entry]) => {
      savedLocalizedBasicInfo[lang] = {
        title: sanitizeTitle(entry?.title),
        description: entry?.description ?? '',
      };
    });

    const currentFormValues = formMethods.getValues();
    formMethods.reset(currentFormValues, {
      keepErrors: true,
      keepIsValid: true,
      keepTouched: true,
      keepSubmitCount: true,
      keepDirty: false,
    });

    const normalizedSnapshot = JSON.stringify({
      title: sanitizeTitle(currentFormValues.title),
      description: currentFormValues.description ?? '',
      level: currentFormValues.level ?? '',
      isPublic: currentFormValues.isPublic ?? true,
      allowComments: currentFormValues.allowComments ?? true,
      defaultLanguage: defaultLanguageCurrent,
      supportedLanguages: supportedLanguagesCurrent,
      priceAmount: typeof currentFormValues.priceAmount === 'number'
        ? currentFormValues.priceAmount
        : Number(currentFormValues.priceAmount ?? 0),
      priceCurrency: currentFormValues.priceCurrency ?? 'KRW',
      isFree: currentFormValues.isFree ?? ((currentFormValues.priceAmount ?? 0) <= 0),
    });
    lastAutosavedSnapshotRef.current = normalizedSnapshot;

    lastSavedBasicInfoRef.current = {
      title: sanitizeTitle(currentFormValues.title),
      description: currentFormValues.description ?? '',
      level: currentFormValues.level ?? '',
      thumbnailUrl: currentFormValues.thumbnailUrl,
      isPublic: currentFormValues.isPublic ?? true,
      allowComments: currentFormValues.allowComments ?? true,
      defaultLanguage: defaultLanguageCurrent,
      supportedLanguages: supportedLanguagesCurrent,
      localizedBasicInfo: savedLocalizedBasicInfo,
      activeBasicInfoLanguage: currentBasicInfoLanguageRef.current,
      draftVersion: currentFormValues.draftVersion ?? null,
      priceAmount: typeof currentFormValues.priceAmount === 'number'
        ? currentFormValues.priceAmount
        : Number(currentFormValues.priceAmount ?? 0),
      priceCurrency: currentFormValues.priceCurrency ?? 'KRW',
      isFree: currentFormValues.isFree ?? ((currentFormValues.priceAmount ?? 0) <= 0),
    };

    const store = useCurriculumStore.getState();
    const snapshots = store.getLanguageSnapshots(currentCourseId);
    const normalizedSnapshots: Record<string, SectionData[]> = {};

    Object.entries(snapshots).forEach(([languageCode, snapshot]) => {
      const normalizedLang = languageCode === 'default'
        ? normalizedDefault
        : normalizeLanguageCode(languageCode) ?? languageCode;
      normalizedSnapshots[normalizedLang] = cloneSectionsShallow(snapshot?.sections || []);
    });

    if (!normalizedSnapshots[normalizedDefault]) {
      normalizedSnapshots[normalizedDefault] = cloneSectionsShallow(store.sections || []);
    }

    lastSavedCurriculumSnapshotsRef.current = normalizedSnapshots;
    persistCurriculumSnapshots(currentCourseId, normalizedSnapshots);

    Object.entries(normalizedSnapshots).forEach(([languageCode, sections]) => {
      primeLanguageSnapshot(currentCourseId, languageCode, {
        sections,
        isCurriculumDirty: false,
        lastModifiedLocallyTimestamp: Date.now(),
      });
    });

    persistTranslationsSnapshot();
    setCurriculumDirty(false);
  }, [
    currentCourseId,
    formMethods,
    primeLanguageSnapshot,
    setCurriculumDirty,
    persistTranslationsSnapshot,
    persistCurriculumSnapshots,
  ]);

  const resetAllChanges = useCallback(async () => {
    if (currentCourseId && currentCourseId !== 'new') {
      try {
        const latest = await coreLogic.actions.loadCourseDetails();
        const data = (latest ?? coreLogic.courseData) as CourseDraftDetailResponse | undefined;
        processedCourseSignatureRef.current = null;
        if (data) {
          applyCourseData(data, { force: true });
          captureCurrentStateAsSaved();
          return;
        }
      } catch (error) {
        console.warn('[useCourseEditLogic] resetAllChanges loadCourseDetails 실패', error);
      }
    }

    const savedBasic = lastSavedBasicInfoRef.current;
    const savedSnapshots = lastSavedCurriculumSnapshotsRef.current;

    if (savedBasic && Object.keys(savedSnapshots).length > 0) {
      const normalizedDefault = normalizeLanguageCode(savedBasic.defaultLanguage) ?? 'ko';
      const baseSupportedLanguages = savedBasic.supportedLanguages && savedBasic.supportedLanguages.length > 0
        ? savedBasic.supportedLanguages
        : Object.keys(savedSnapshots);

      const syntheticData: CourseDraftDetailResponse = {
        id: currentCourseId ?? '',
        title: savedBasic.title,
        description: savedBasic.description ?? '',
        level: savedBasic.level as any,
        thumbnailUrl: savedBasic.thumbnailUrl ?? undefined,
        isPublic: savedBasic.isPublic,
        allowComments: savedBasic.allowComments,
        price: (savedBasic.isFree ?? ((savedBasic.priceAmount ?? 0) <= 0))
          ? null
          : {
              amount: savedBasic.priceAmount ?? 0,
              currency: (savedBasic.priceCurrency ?? 'KRW').toUpperCase(),
            },
        sections: convertSectionsToDraft(savedSnapshots[normalizedDefault] ?? []),
      } as CourseDraftDetailResponse;

      const localizedSectionsPayload: Record<string, unknown> = {};
      Object.entries(savedSnapshots).forEach(([languageCode, sections]) => {
        localizedSectionsPayload[languageCode] = convertSectionsToDraft(sections);
      });

      const syntheticDataExtended = {
        ...syntheticData,
        defaultLanguage: savedBasic.defaultLanguage ?? normalizedDefault,
        supportedLanguages: baseSupportedLanguages,
        localizedBasicInfo: savedBasic.localizedBasicInfo ?? {},
        localizedSections: localizedSectionsPayload,
        updatedAt: new Date().toISOString(),
        draftVersion: savedBasic.draftVersion ?? null,
        price: (savedBasic.isFree ?? ((savedBasic.priceAmount ?? 0) <= 0))
          ? null
          : {
              amount: savedBasic.priceAmount ?? 0,
              currency: (savedBasic.priceCurrency ?? 'KRW').toUpperCase(),
            },
      } as CourseDraftDetailResponse;

      processedCourseSignatureRef.current = null;
      applyCourseData(syntheticDataExtended, { force: true });
      captureCurrentStateAsSaved();
      return;
    }

    const defaultLanguage = 'ko';
    processedCourseSignatureRef.current = null;
    defaultLanguageRef.current = defaultLanguage;
    currentBasicInfoLanguageRef.current = defaultLanguage;
    basicInfoTranslationsRef.current = {
      [defaultLanguage]: { title: '제목 없음', description: '' },
    };
    setBasicInfoAvailableLanguages([defaultLanguage]);
    setCurrentBasicInfoLanguage(defaultLanguage);
    bumpBasicInfoTranslationsVersion();
    reset({
      title: '제목 없음',
      description: '',
      level: '',
      thumbnailUrl: undefined,
      isPublic: true,
      allowComments: true,
      defaultLanguage,
      supportedLanguages: [defaultLanguage],
    });
    coreLogic.actions.setThumbnailPreview('');
    setInitialSections([]);
    setInitialLocalizedSections(undefined);
    syncBasicInfoLanguageMetadata([defaultLanguage], defaultLanguage);
    setCurriculumDirty(false);

    resetCurriculumState();
    setResetSignal(prev => prev + 1);
    persistTranslationsSnapshot();
  }, [
    applyCourseData,
    bumpBasicInfoTranslationsVersion,
    captureCurrentStateAsSaved,
    coreLogic.actions,
    coreLogic.courseData,
    currentCourseId,
    convertSectionsToDraft,
    persistTranslationsSnapshot,
    reset,
    setBasicInfoAvailableLanguages,
    setCurriculumDirty,
    setCurrentBasicInfoLanguage,
    setInitialLocalizedSections,
    setInitialSections,
    syncBasicInfoLanguageMetadata,
    resetCurriculumState,
    lastSavedBasicInfoRef,
    lastSavedCurriculumSnapshotsRef,
  ]);

  // 폼 제출 핸들러
  const onSubmit = useCallback(async (data: BasicInfoFormData) => {
    syncCurrentBasicInfo();

    const supportedLanguagesRaw = formMethods.getValues('supportedLanguages') ?? [];
    const defaultLanguageRaw = formMethods.getValues('defaultLanguage');

    syncBasicInfoLanguageMetadata(supportedLanguagesRaw, defaultLanguageRaw);

    const normalizedDefault = normalizeLanguageCode(defaultLanguageRaw) ?? defaultLanguageRef.current ?? 'ko';

    const localizedBasicInfoPayload: Record<string, BasicInfoTranslationEntry> = {};
    supportedLanguagesRaw.forEach((languageCode) => {
      const normalized = normalizeLanguageCode(languageCode) ?? languageCode;
      const entry = basicInfoTranslationsRef.current[normalized] ?? { title: '', description: '' };
      localizedBasicInfoPayload[languageCode] = {
        title: entry.title ?? '',
        description: entry.description ?? '',
      };
    });

    const defaultEntryNormalized = basicInfoTranslationsRef.current[normalizedDefault] ?? { title: '', description: '' };

    const payloadWithLocalization: BasicInfoFormData = {
      ...data,
      title: defaultEntryNormalized.title ?? data.title ?? '',
      description: defaultEntryNormalized.description ?? data.description ?? '',
      localizedBasicInfo: localizedBasicInfoPayload,
      activeBasicInfoLanguage: currentBasicInfoLanguageRef.current,
    };

    await coreLogic.actions.handleSaveAll(payloadWithLocalization);
    captureCurrentStateAsSaved();

    // 저장 성공 시 임시저장 데이터 삭제 및 커리큘럼 상태 초기화
    if (currentCourseId) {
      clearAutosaveData(currentCourseId);
      setHasUnsavedChanges(false);
      setCurriculumDirty(false); // 커리큘럼 dirty 상태도 명시적으로 초기화
      handledAutosavePrompts.delete(currentCourseId);
    }

    const latestValues = formMethods.getValues();
    formMethods.reset(
      {
        ...latestValues,
        title: sanitizeTitle(latestValues.title),
      },
      {
        keepDirty: false,
        keepDefaultValues: false,
      },
    );
  }, [
    captureCurrentStateAsSaved,
    coreLogic.actions,
    currentCourseId,
    formMethods,
    sanitizeTitle,
    setCurriculumDirty,
    syncBasicInfoLanguageMetadata,
    syncCurrentBasicInfo,
  ]);
  
  return {
    ...coreLogic,
    formMethods,
    onSubmit,
    initialSections,
    initialLocalizedSections,
    currentBasicInfoLanguage,
    basicInfoAvailableLanguages,
    basicInfoTranslationSummary,
    changeBasicInfoLanguage,
    copyBasicInfoFromDefault,
    syncBasicInfoLanguageMetadata,
    resetAllChanges,
    resetSignal,
    hasUnsavedChanges,
    bypassNextBeforeUnload,
    autosaveRestoreModal: {
      isOpen: autosaveModalState.isOpen,
      courseId: autosaveModalState.courseId,
      onConfirm: () => {
        const courseId = autosaveModalState.courseId;
        if (courseId) {
          restoreAutosaveData(courseId);
          if (pendingAutosaveTimestampRef.current !== null) {
            handledAutosavePrompts.set(courseId, pendingAutosaveTimestampRef.current);
          }
        }
        setAutosaveModalState({ isOpen: false, courseId: null });
        pendingAutosaveTimestampRef.current = null;
      },
      onDiscard: () => {
        const courseId = autosaveModalState.courseId;
        if (courseId) {
          clearAutosaveData(courseId);
          if (pendingAutosaveTimestampRef.current !== null) {
            handledAutosavePrompts.set(courseId, pendingAutosaveTimestampRef.current);
          }
        }
        setAutosaveModalState({ isOpen: false, courseId: null });
        pendingAutosaveTimestampRef.current = null;
      },
    },
    flushAutosaveSnapshot,
  };
};
